<template>
  <div class="bg-white rounded-lg border border-gray-200">
    <!-- Month nav -->
    <div class="flex items-center justify-between px-3 py-2 border-b border-gray-100">
      <button class="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100" @click="prevMonth">
        <font-awesome-icon :icon="['fas', 'chevron-left']" class="text-xs" />
      </button>
      <div class="flex items-center gap-2">
        <span class="text-sm font-semibold text-gray-800">{{ monthLabel }}</span>
        <button
          class="text-[10px] font-medium text-primary-600 hover:text-primary-700 px-1.5 py-0.5 rounded hover:bg-primary-50"
          @click="goToday"
        >Today</button>
      </div>
      <button class="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100" @click="nextMonth">
        <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-xs" />
      </button>
    </div>

    <!-- Day-of-week headers -->
    <div class="grid grid-cols-7 px-2 pt-2">
      <div
        v-for="d in weekDays"
        :key="d"
        class="text-center text-[11px] uppercase tracking-wider text-gray-500 font-semibold py-1"
      >{{ d }}</div>
    </div>

    <!-- Calendar skeleton -->
    <div v-if="loading" class="grid grid-cols-7 gap-px px-2 pb-2">
      <div
        v-for="n in 35"
        :key="'skel-' + n"
        class="w-full aspect-square flex items-center justify-center rounded"
      >
        <div class="w-5 h-3 bg-gray-200 rounded animate-pulse"></div>
      </div>
    </div>

    <!-- Day cells -->
    <div v-else class="grid grid-cols-7 gap-0.5 px-2 pb-2">
      <button
        v-for="(day, i) in calendarDays"
        :key="i"
        class="relative w-full aspect-square flex flex-col items-center justify-center rounded text-sm transition-colors overflow-hidden"
        :class="dayClasses(day)"
        :style="dayHeatStyle(day)"
        :disabled="day.isPast && !day.isToday"
        @click="pickDay(day)"
      >
        <span :class="dayTextClasses(day)">{{ day.dayNumber }}</span>
      </button>
    </div>

    <!-- Time picker -->
    <div v-if="!dateOnly" class="flex items-center gap-2.5 px-3 py-3 border-t border-gray-100">
      <font-awesome-icon :icon="['fas', 'clock']" class="text-sm text-gray-400" />
      <select
        v-model="selectedHour"
        class="px-2 py-1.5 border border-gray-300 rounded text-sm text-gray-700"
        @change="emitValue"
      >
        <option v-for="h in 12" :key="h" :value="h">{{ h }}</option>
      </select>
      <span class="text-sm text-gray-500">:</span>
      <select
        v-model="selectedMinute"
        class="px-2 py-1.5 border border-gray-300 rounded text-sm text-gray-700"
        @change="emitValue"
      >
        <option v-for="m in minuteOptions" :key="m" :value="m">{{ String(m).padStart(2, '0') }}</option>
      </select>
      <select
        v-model="selectedAmPm"
        class="px-2 py-1.5 border border-gray-300 rounded text-sm text-gray-700"
        @change="emitValue"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>

    <!-- Legend -->
    <div class="flex items-center justify-center gap-1 px-3 py-2 border-t border-gray-100">
      <span class="text-xs text-gray-500 mr-1">Free</span>
      <span class="w-5 h-3 rounded-sm bg-white border border-gray-300"></span>
      <span class="w-5 h-3 rounded-sm" style="background-color: rgb(255, 238, 228)"></span>
      <span class="w-5 h-3 rounded-sm" style="background-color: rgb(255, 221, 210)"></span>
      <span class="w-5 h-3 rounded-sm" style="background-color: rgb(255, 195, 175)"></span>
      <span class="w-5 h-3 rounded-sm" style="background-color: rgb(247, 103, 52)"></span>
      <span class="text-xs text-gray-500 ml-1">Busy</span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { fetchPatientWorkQueue } from '@/services/api/patientWorkQueue';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  format,
  isSameDay,
  isToday,
  isBefore,
  isSameMonth,
} from 'date-fns';

let _cachedTasksByDate = null;
let _cachePromise = null;

export default {
  name: 'TaskCalendarPicker',
  components: { FontAwesomeIcon },
  props: {
    modelValue: { type: String, default: '' },
    dateOnly: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'day-picked'],
  data() {
    return {
      currentMonth: new Date(),
      weekDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      tasksByDate: {},
      loading: true,
      pickedDate: null,
      selectedHour: 9,
      selectedMinute: 0,
      selectedAmPm: 'AM',
      minuteOptions: Array.from({ length: 60 }, (_, i) => i),
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    monthLabel() {
      return format(this.currentMonth, 'MMMM yyyy');
    },
    selectedDate() {
      if (this.pickedDate) return this.pickedDate;
      if (!this.modelValue) return null;
      return this.dateFromValue(this.modelValue);
    },
    calendarDays() {
      const monthStart = startOfMonth(this.currentMonth);
      const monthEnd = endOfMonth(this.currentMonth);
      const gridStart = startOfWeek(monthStart);
      const gridEnd = endOfWeek(monthEnd);
      const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
      const today = new Date();

      return days.map((date) => {
        const key = format(date, 'yyyy-MM-dd');
        const tasks = this.tasksByDate[key] || [];
        return {
          date,
          dayNumber: date.getDate(),
          isCurrentMonth: isSameMonth(date, this.currentMonth),
          isToday: isToday(date),
          isSelected: this.selectedDate && isSameDay(date, this.selectedDate),
          taskCount: tasks.length,
          hasOverdue: tasks.some((t) => t.isOverdue),
          isPast: isBefore(date, today) && !isToday(date),
        };
      });
    },
    maxTaskCount() {
      let max = 0;
      for (const day of this.calendarDays) {
        if (day.isCurrentMonth && day.taskCount > max) max = day.taskCount;
      }
      return max;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        if (!val) return;
        const d = this.dateFromValue(val);
        if (isNaN(d.getTime())) return;
        this.pickedDate = d;
        let h = d.getHours();
        this.selectedAmPm = h >= 12 ? 'PM' : 'AM';
        if (h === 0) h = 12;
        else if (h > 12) h -= 12;
        this.selectedHour = h;
        this.selectedMinute = d.getMinutes();
      },
    },
  },
  async mounted() {
    if (_cachedTasksByDate) {
      this.tasksByDate = _cachedTasksByDate;
      this.loading = false;
      return;
    }
    if (_cachePromise) {
      this.tasksByDate = await _cachePromise;
      this.loading = false;
      return;
    }
    _cachePromise = this.fetchTaskHeatmap();
    this.tasksByDate = await _cachePromise;
    this.loading = false;
  },
  methods: {
    dateFromValue(value) {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
      if (match) {
        const [, year, month, day] = match;
        return new Date(Number(year), Number(month) - 1, Number(day));
      }
      return new Date(value);
    },
    async fetchTaskHeatmap() {
      try {
        const { data: entityData } = await this.$axios.get('/api/entities/appuser-entity/');
        const entities = entityData.user_entities || entityData;
        if (!entities.length) return {};
        const entityId = entities[0].id;

        const data = await fetchPatientWorkQueue({
          start_row: 0,
          end_row: 999999,
          task_owner_id: entityId,
          filter_args: {
            metric_ids: [],
            show_completed: false,
            include_snoozed: false,
          },
        });
        if (data?.success) {
          const map = {};
          for (const patient of data.rowData || []) {
            for (const task of patient.tasks || []) {
              if (!task.due_date) continue;
              const key = task.due_date;
              if (!map[key]) map[key] = [];
              map[key].push({ isOverdue: task.is_overdue });
            }
          }
          _cachedTasksByDate = map;
          return map;
        }
      } catch {
        // Heatmap is optional — fail silently
      }
      _cachedTasksByDate = {};
      return {};
    },
    prevMonth() {
      this.currentMonth = subMonths(this.currentMonth, 1);
    },
    nextMonth() {
      this.currentMonth = addMonths(this.currentMonth, 1);
    },
    goToday() {
      this.currentMonth = new Date();
      this.pickedDate = new Date();
      this.emitValue();
    },
    pickDay(day) {
      if (day.isPast && !day.isToday) return;
      this.pickedDate = day.date;
      this.emitValue();
      this.$emit('day-picked');
    },
    emitValue() {
      if (!this.pickedDate) return;
      const d = new Date(this.pickedDate);
      const pad = (n) => String(n).padStart(2, '0');
      if (this.dateOnly) {
        this.$emit('update:modelValue', `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
        return;
      }
      let h = this.selectedHour;
      if (this.selectedAmPm === 'PM' && h !== 12) h += 12;
      if (this.selectedAmPm === 'AM' && h === 12) h = 0;
      d.setHours(h, this.selectedMinute, 0, 0);
      const val = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      this.$emit('update:modelValue', val);
    },
    dayClasses(day) {
      return {
        'ring-2 ring-blue-400': day.isSelected,
        'ring-2 ring-[#F76734]': day.isToday && !day.isSelected,
        'opacity-30 cursor-not-allowed': day.isPast && !day.isToday,
        'opacity-40': !day.isCurrentMonth,
        'hover:ring-2 hover:ring-gray-300': !day.isSelected && !day.isToday && !(day.isPast && !day.isToday),
        'cursor-pointer': !(day.isPast && !day.isToday),
      };
    },
    dayTextClasses(day) {
      return {
        'text-gray-900 font-medium': day.isCurrentMonth && !day.isToday,
        'text-[#F76734] font-bold': day.isToday,
        'text-gray-400': !day.isCurrentMonth,
      };
    },
    dayHeatStyle(day) {
      if (!day.isCurrentMonth || day.taskCount === 0 || (day.isPast && !day.isToday)) {
        return {};
      }
      const intensity = this.maxTaskCount > 0
        ? Math.min(day.taskCount / this.maxTaskCount, 1)
        : 0;
      const r = 255;
      const g = Math.round(255 - intensity * (255 - 221));
      const b = Math.round(255 - intensity * (255 - 210));
      return { backgroundColor: `rgb(${r}, ${g}, ${b})` };
    },
  },
};
</script>
